import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DndContext } from '../../components/DndKit';

import './CollectionsPage.css';

import { buildRoute as buildCollectionRoute } from './CollectionsPage.Route';
import { useAuth } from '../../hook/useAuth';
import { useSample } from '../../hook/useSample';
import { useStorage } from '../../hook/useStorage';
import Layout from '../../layouts/layouts';
import ActiveSamplePanel from './ActiveSamplePanel/ActiveSamplePanel';
import ConfirmModal from '../../components/Modals/ConfirmModal/ConfirmModal';
import ModifyStorageModal from '../../components/Modals/ModifyStorageModal/ModifyStorageModal';
import MoveSamplesModal from '../../components/Modals/MoveSamplesModal/MoveSamplesModal';
import StorageListPanel from './StorageListPanel/StorageListPanel';
import StorageSamplesPanel from './StorageSamplesPanel/StorageSamplesPanel';

/**
 * Collections management
 * @component
 */
const CollectionsPage = ({ layout }) => {
	const navigate = useNavigate();

	const { authenticated, user, signin } = useAuth();

	const { activeStorage, storages, addStorage, updateStorage, deleteStorage, exportStorage, loadingStorages } =
		useStorage();

	const {
		activeSample,
		samples,
		selectSample,
		//setUpdatedSample,
		//loadingSamples,
	} = useSample();

	const [showAddModal, setShowAddModal] = useState(false);
	const [showModifyModal, setShowModifyModal] = useState(false);
	const [showMoveModal, setShowMoveModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	useEffect(() => {
		console.log('Initialization: CollectionsPage');
	}, []);

	useEffect(() => {
		// If storage deselected => Deselect sample
		if (!activeStorage) {
			selectSample(null);
			//setUpdatedSample(null);
		}
	}, [activeStorage, selectSample]);

	let showActiveSample = activeSample ? true : false;
	let showStoragePanel = false;
	let isOwnActiveStorage = false;

	if (activeStorage) {
		showStoragePanel = activeStorage.owner_id === user?.id ? false : true;
		isOwnActiveStorage = activeStorage.owner_id === user?.id ? true : false;
	}

	const title = activeStorage ? 'Collection — ' + activeStorage.name : 'Collection';

	return (
		<Layout layout={layout} title={title}>
			<div className="collections">
				<DndContext>
					{!showActiveSample && !showStoragePanel ? (
						<StorageListPanel
							loading={loadingStorages}
							storages={storages}
							onAddStorageReq={handleAddStorage}
							onDeleteStorageReq={handleDeleteStorage}
							onExportStorageReq={processExportStorage}
							onModifyStorageReq={handleModifyStorage}
							onMoveSamplesReq={handleMoveSamples}
						/>
					) : null}
					{showActiveSample ? (
						<ActiveSamplePanel
							sample={activeSample}
							onSelect={handleActivateSample}
							onUpdate={handleUpdateSample}
						/>
					) : null}
					{activeStorage ? (
						<StorageSamplesPanel
							isOwned={isOwnActiveStorage}
							onActivateSample={handleActivateSample}
							onUpdateSample={handleUpdateSample}
						/>
					) : null}
					<ModifyStorageModal
						isNew={true}
						isOpen={showAddModal}
						storage={null}
						title="Add Storage"
						onResult={processAddStorage}
					/>
					<ModifyStorageModal
						isNew={false}
						isOpen={showModifyModal}
						storage={activeStorage}
						title="Modify Storage"
						onResult={processModifyStorage}
					/>
					<MoveSamplesModal
						default_target={activeStorage}
						isOpen={showMoveModal}
						samples={samples}
						onCancel={handleCloseMoveModal}
						onConfirm={handleCloseMoveModal}
					/>
					<ConfirmModal
						className="delete-strorage"
						defaultYes={false}
						isOpen={showDeleteModal}
						title="Delete storage"
						onResult={processDeleteStorage}
					>
						<>
							Are you sure you want to delete the storage?
							<div className="storage-name">{activeStorage ? activeStorage.name : ''}</div>
						</>
					</ConfirmModal>
				</DndContext>
			</div>
		</Layout>
	);

	function handleActivateSample(sample) {
		if (
			!sample || // Deselect
			!activeSample || // No active sample
			activeSample.id !== sample.id
		) {
			// Different sample activated
			navigate(buildCollectionRoute(activeStorage, sample));
		}
	}

	function handleUpdateSample(sample) {
		handleActivateSample(sample);
	}

	function handleAddStorage() {
		if (authenticated) {
			setShowAddModal(true);
		} else {
			signin();
		}
	}

	function handleModifyStorage() {
		setShowModifyModal(true);
	}

	function handleMoveSamples() {
		setShowMoveModal(true);
	}

	function handleCloseMoveModal() {
		setShowMoveModal(false);
	}

	function processExportStorage() {
		exportStorage(activeStorage);
	}

	function handleDeleteStorage() {
		setShowDeleteModal(true);
	}

	function processAddStorage(result) {
		if (result.status) {
			addStorage(result.data.name, result.data.shortname, result.data.note, (newStorage) => {
				let storageUrl = buildCollectionRoute(newStorage);
				navigate(storageUrl);
			});
		}
		setShowAddModal(false);
	}

	function processModifyStorage(result) {
		if (result.status) {
			updateStorage(
				activeStorage,
				result.data.name,
				result.data.shortname,
				result.data.note,
				result.data.hidden,
				result.data.public,
				(updStorage) => {
					let storageUrl = buildCollectionRoute(updStorage);
					navigate(storageUrl);
				}
			);
		}
		setShowModifyModal(false);
	}

	function processDeleteStorage(result) {
		setShowDeleteModal(false);
		if (result) {
			deleteStorage(activeStorage, () => {
				navigate(buildCollectionRoute(null));
			});
		}
	}
};

CollectionsPage.propTypes = {
	// Page Layout
	layout: PropTypes.string.isRequired,
};

export default CollectionsPage;
